import { required, email, maxLength } from 'vuelidate/lib/validators';
import { name } from '@/validators/name';
import { houseNumber } from '@/validators/houseNumber';
import { FormOption } from '@/types/forms/formOption';
import { TextField } from '@/types/forms/fields/textField';
import { SelectField } from '@/types/forms/fields/selectField';
import { CheckboxField } from '@/types/forms/fields/checkboxField';
import { ZipAndCityFormGroup } from '@/types/forms/formGroups/zipAndCityFormGroup';
import { StreetAndHousenumberFormGroup } from '@/types/forms/formGroups/streetAndHousenumberFormGroup';
import { PlantBuilderFormStep } from '@/types/forms/formSteps/plantBuilderFormStep';
import { FirstAndLastNameFormGroup } from '@/types/forms/formGroups/firstAndLastNameFormGroup';
import { positiveInteger } from '@/validators/positiveInteger';
import { zip } from '@/validators/zip';
import { authService } from '@/services/authService';
import { contentServices } from '@/services/contentService';

const installerOptionKeySeparator = '|';

/**
 * For installer users the option key combines installerId and companyId,
 * because the same company can belong to multiple installers.
 * For end-users the key is just the companyId.
 */
export function buildInstallerOptionKey(
    installerId: string,
    companyId: string,
): string {
    return `${installerId}${installerOptionKeySeparator}${companyId}`;
}

export function parseInstallerOptionKey(
    key: string,
): { installerId: string; companyId: string } {
    const separatorIndex = key.indexOf(installerOptionKeySeparator);

    if (separatorIndex === -1) {
        return { installerId: '', companyId: key };
    }

    return {
        installerId: key.slice(0, separatorIndex),
        companyId: key.slice(separatorIndex + 1),
    };
}

export const plantBuilderFormStep = new PlantBuilderFormStep({
    installer: new SelectField<string>({
        excludeFromDto: true,
        excludeFromSchema: true,
        hasServiceDrivenOptions: true,
        onMountOptionsSetter: async () => {
            if (authService.deaAdditionalUserData?.isInstaller) {
                const entries = authService.deaAdditionalUserData.installers.flatMap(
                    (installer) =>
                        installer.companies.map((company) => ({
                            installer,
                            company,
                        })),
                );

                entries.sort(
                    (a, b) =>
                        a.company.companyName.localeCompare(
                            b.company.companyName,
                        ) || a.company.city.localeCompare(b.company.city),
                );

                return entries.map(
                    (entry) =>
                        new FormOption({
                            key: buildInstallerOptionKey(
                                entry.installer.id,
                                entry.company.id,
                            ),
                        }),
                );
            }

            const content = contentServices.form.content;
            if (!content) {
                throw 'Content unavailable';
            }

            const installersContent =
                content.serviceDrivenContent.installersContent;
            if (!installersContent) {
                // No company has a matching installer. This is a valid state:
                // the user has to use the "not in list" checkbox instead.
                return [];
            }

            const companies = [...installersContent].sort(
                (a, b) =>
                    a.companyName.localeCompare(b.companyName) ||
                    a.city.localeCompare(b.city),
            );

            return companies.map(
                (company) =>
                    new FormOption({
                        key: company.id,
                    }),
            );
        },
        validators: {
            required,
        },
    }),
    installerNotListed: new CheckboxField(),
    companyId: new TextField(),
    installerId: new TextField(),
    companyName: new TextField({
        validators: {
            required,
            maxLength: maxLength(100),
        },
        userChangeable: false,
    }),
    builderName: new FirstAndLastNameFormGroup({
        firstName: new TextField({
            validators: {
                name,
            },
            userChangeable: false,
        }),
        lastName: new TextField({
            validators: {
                name,
            },
            userChangeable: false,
        }),
    }),
    country: new SelectField({
        value: 'deu',
        options: [
            new FormOption({ key: 'deu' }),
            new FormOption({ key: 'aut' }),
            new FormOption({ key: 'dnk' }),
            new FormOption({ key: 'fra' }),
            new FormOption({ key: 'nld' }),
            new FormOption({ key: 'pol' }),
            new FormOption({ key: 'che' }),
        ],
        userChangeable: false,
    }),
    zipAndCity: new ZipAndCityFormGroup({
        zip: new TextField({
            validators: {
                required,
                zip: zip('application.plantBuilder.country'),
            },
            userChangeable: false,
        }),
        city: new TextField({
            validators: {
                required,
            },
            userChangeable: false,
        }),
    }),
    streetAndHousenumber: new StreetAndHousenumberFormGroup({
        street: new TextField({
            validators: {
                required,
            },
            userChangeable: false,
        }),
        housenumber: new TextField({
            validators: {
                required,
                houseNumber,
                maxLength: maxLength(8),
            },
            userChangeable: false,
        }),
    }),
    email: new TextField({
        validators: {
            required,
            email,
        },
        userChangeable: false,
    }),
    phoneNumber1: new TextField({
        validators: {
            required,
            phoneNumber: positiveInteger,
        },
        userChangeable: false,
    }),
    phoneNumber2: new TextField({
        validators: {
            phoneNumber: positiveInteger,
        },
        userChangeable: false,
    }),
});
